import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { productsApi } from '@/api/endpoints'
import { unitName, unitsFor } from '@/lib/units'
import { money } from '@/lib/format'
import { estimateLine, rateFor } from '@/lib/pricing'
import { describeMinimum, isShort, startingQuantity, stepFor, useCategoryMinimums } from '@/lib/minimums'
import QuantityStepper from '@/components/common/QuantityStepper'

/**
 * Put a product the customer never named onto an open order.
 *
 * The case this exists for: they rang to add something, or asked for a size
 * in words nothing in the catalog matches and the operator knows which SKU
 * they meant. Until now the only way was to cancel and re-enter the whole
 * order by hand.
 *
 * No alias is learned from it, unlike a correction. Nobody said any
 * particular wording here, so there is nothing to teach.
 */
// `tier` is the customer's price tier, for the price shown; without one
// (no customer chosen yet) the picker says so instead of guessing.
export default function AddProduct({ saving, onAdd, onCancel, tier }) {
  const [search, setSearch] = useState('')
  const [product, setProduct] = useState(null)
  const [quantity, setQuantity] = useState('1')
  const [unit, setUnit] = useState('')
  const term = search.trim()
  const minimums = useCategoryMinimums()

  const { data, isFetching } = useQuery({
    queryKey: ['product-search', term],
    queryFn: () => productsApi.list({ search: term, page_size: 8, is_active: true }),
    enabled: term.length >= 2,
    staleTime: 60_000,
  })

  // The same floor the customer's own page enforces and the API requires: a
  // line of nothing is an unanswered question, not an order.
  const amount = Number(quantity)
  const ready = product && Number.isFinite(amount) && amount > 0

  return (
    <div className="line-row add-line">
      <div className="add-line-body">
        <div className="line-label">Add a product</div>

        {product ? (
          <p className="add-line-chosen">
            <strong>{product.item_name}</strong>
            <span className="line-code">{product.item_code}</span>
            <button type="button" className="btn-link" onClick={() => setProduct(null)}>
              change
            </button>
          </p>
        ) : (
          <>
            <input
              autoFocus
              type="search"
              value={search}
              placeholder="Search by name or item code, e.g. 2 x 12 or MSWN"
              onChange={(e) => setSearch(e.target.value)}
            />
            {term.length >= 2 && (
              <div className="candidate-list">
                {isFetching && <p className="muted">Searching…</p>}
                {!isFetching && data?.items?.length === 0 && (
                  <p className="muted">Nothing matches “{term}”.</p>
                )}
                {data?.items?.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className="candidate"
                    onClick={() => {
                      setProduct(p)
                      // Starts at the category's minimum order, in kg, where
                      // there is one — a starting point, freely changed.
                      setQuantity(String(startingQuantity(p, minimums)))
                      // Its own pricing unit, which is the one the rate below
                      // is quoted in — anything else has to be converted and
                      // is a decision, not a default.
                      setUnit(p.base_unit ?? 'KILOGRAMS')
                    }}
                  >
                    <span className="candidate-name">{p.item_name}</span>
                    <span className="candidate-meta">
                      {p.item_code}
                      {tier && rateFor(p, tier) != null &&
                        ` · ${money(rateFor(p, tier))}/${unitName(p.base_unit)}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {product && (
          <div className="add-line-fields">
            <label>
              <span className="line-label">Quantity</span>
              <QuantityStepper
                value={quantity}
                onChange={setQuantity}
                step={stepFor(product, unit, minimums)}
                unit={String(unit ?? '').toLowerCase()}
              />
            </label>
            {/* This SKU's own two units. A product with no secondary unit
                offers one row, which is the truth about it — there is no
                other unit it can be sold by. */}
            <label>
              <span className="line-label">Unit</span>
              <select value={unit} onChange={(e) => setUnit(e.target.value)}>
                {unitsFor(product).map((u) => (
                  <option key={u} value={u}>
                    {unitName(u)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {product && <PricePreview product={product} tier={tier} quantity={quantity} unit={unit} />}

        {/* Warned, not refused: staff may take an order below the minimum,
            but the order cannot be confirmed until it is corrected. */}
        {product && isShort(product, quantity, unit, minimums) && (
          <p className="hint caution">
            Below the minimum order of {describeMinimum(product, minimums)}. It can be added,
            but the order cannot be confirmed until the quantity is increased.
          </p>
        )}

        <div className="add-line-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!ready || saving}
            onClick={() => onAdd({ product_id: product.id, quantity: amount, unit }, product)}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
          <button type="button" className="btn btn-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

function PricePreview({ product, tier, quantity, unit }) {
  if (!tier) {
    return <p className="hint add-line-price">Choose the customer to see their price.</p>
  }
  const { rate, amount } = estimateLine(product, tier, quantity, unit)
  if (rate == null) {
    return (
      <p className="hint add-line-price caution">
        No Tier {tier} rate for this product — the line will need a price.
      </p>
    )
  }
  return (
    <p className="add-line-price">
      <span className="muted">
        {money(rate)} per {unitName(product.base_unit)} · Tier {tier}
        {product.inclusive_of_tax ? ' · incl. GST' : ' + GST'}
      </span>
      {amount != null ? (
        <strong>{money(amount)}</strong>
      ) : (
        <span className="muted">Amount worked out when added</span>
      )}
    </p>
  )
}
